import React, { useState, useEffect, useRef } from 'react';
import {
  SUPPORTED_COUNTRIES,
  CountryCode,
  formatPhoneInput,
  validateAndNormalizePhone,
  getPhoneDigits,
} from '../../utils/phone';

interface PhoneInputProps {
  value: string;
  onChange: (value: string, isValid: boolean, normalized: string | null) => void;
  countryCode: CountryCode;
  onCountryChange: (code: CountryCode) => void;
  placeholder?: string;
  disabled?: boolean;
  error?: string;
  label?: string;
  required?: boolean;
  id?: string;
}

const PhoneInput: React.FC<PhoneInputProps> = ({
  value,
  onChange,
  countryCode,
  onCountryChange,
  placeholder = '(555) 123-4567',
  disabled = false,
  error,
  label,
  required = false,
  id = 'phone',
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [internalError, setInternalError] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;
    const digits = getPhoneDigits(rawValue);

    // Limit to max digits for the country
    const country = SUPPORTED_COUNTRIES.find(c => c.code === countryCode);
    const maxDigits = country?.maxDigits || 10;

    if (digits.length > maxDigits) {
      return;
    }

    const formatted = formatPhoneInput(digits, countryCode);

    // Validate
    const validation = validateAndNormalizePhone(digits, countryCode);

    // Only show error if user has entered enough digits
    if (digits.length >= maxDigits && !validation.isValid) {
      setInternalError(validation.error || 'Invalid phone number');
    } else {
      setInternalError(null);
    }

    onChange(formatted, validation.isValid, validation.normalized);
  };

  const handleCountrySelect = (code: CountryCode) => {
    onCountryChange(code);
    setIsDropdownOpen(false);

    // Re-validate with new country code
    const digits = getPhoneDigits(value);
    const formatted = formatPhoneInput(digits, code);
    const validation = validateAndNormalizePhone(digits, code);
    onChange(formatted, validation.isValid, validation.normalized);
  };

  const getCountryFlag = (code: CountryCode) => {
    switch (code) {
      case '+1':
        return 'US';
      case '+52':
        return 'MX';
      default:
        return '??';
    }
  };

  const displayError = error || internalError;

  return (
    <div className="w-full">
      {label && (
        <label htmlFor={id} className="block text-sm font-medium text-[#344854] mb-1">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      <div className="flex">
        {/* Country Code Dropdown */}
        <div className="relative" ref={dropdownRef}>
          <button
            type="button"
            onClick={() => !disabled && setIsDropdownOpen(!isDropdownOpen)}
            disabled={disabled}
            className={`
              flex items-center justify-center gap-1 px-3 py-2
              border border-r-0 border-[#B2D8C5] rounded-l
              bg-gray-50 text-[#344854] text-sm
              hover:bg-gray-100 transition-colors
              focus:outline-none focus:ring-2 focus:ring-[#3A5E63] focus:ring-inset
              ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
              ${displayError ? 'border-red-300' : ''}
            `}
          >
            <span className="text-base">{getCountryFlag(countryCode)}</span>
            <span className="font-medium">{countryCode}</span>
            <svg
              className={`w-4 h-4 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {isDropdownOpen && (
            <div className="absolute top-full left-0 mt-1 w-48 bg-white border border-[#B2D8C5] rounded shadow-lg z-10">
              {SUPPORTED_COUNTRIES.map((country) => (
                <button
                  key={country.code}
                  type="button"
                  onClick={() => handleCountrySelect(country.code as CountryCode)}
                  className={`
                    w-full flex items-center gap-2 px-3 py-2 text-left text-sm
                    hover:bg-[#E8F5F2] transition-colors
                    ${countryCode === country.code ? 'bg-[#E8F5F2] text-[#3A5E63] font-medium' : 'text-[#344854]'}
                  `}
                >
                  <span className="text-base">{country.flag}</span>
                  <span>{country.name}</span>
                  <span className="ml-auto text-[#344854]/60">{country.code}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Phone Number Input */}
        <input
          type="tel"
          id={id}
          value={value}
          onChange={handleInputChange}
          disabled={disabled}
          placeholder={placeholder}
          className={`
            flex-1 px-4 py-2
            border border-[#B2D8C5] rounded-r
            focus:outline-none focus:ring-2 focus:ring-[#3A5E63]
            ${disabled ? 'bg-gray-100 opacity-50 cursor-not-allowed' : 'bg-white'}
            ${displayError ? 'border-red-300 focus:ring-red-500' : ''}
          `}
        />
      </div>

      {displayError && (
        <p className="mt-1 text-sm text-red-600">{displayError}</p>
      )}
    </div>
  );
};

export default PhoneInput;
